/**
 * 通用Web Components按需加载器
 *
 * 用法：
 * 1. 直接使用默认配置，监控常用组件
 * 2. 可通过window.COMPONENTS_CONFIG覆盖默认配置
 */

// 导入组件配置
import COMPONENTS_CONFIG, { addComponentConfig } from "./components-config.js";

// 已加载的组件记录
const loadedComponents = new Set();

/**
 * 获取要监视的组件配置
 * 支持通过window.COMPONENTS_CONFIG覆盖默认配置
 */
function getComponentsConfig() {
  // 允许通过全局变量覆盖默认配置
  return window.COMPONENTS_CONFIG || COMPONENTS_CONFIG;
}

/**
 * 动态导入组件
 * @param {Object} componentConfig 组件配置对象
 * @returns {Promise}
 */
async function loadComponent(componentConfig) {
  const { tag, path } = componentConfig;

  // 如果组件已加载，直接返回
  if (loadedComponents.has(tag)) return;

  try {
    // 动态导入组件
    await import(path);
    loadedComponents.add(tag);
    console.log(`Component '${tag}' loaded successfully`);
  } catch (error) {
    console.error(`Failed to load component '${tag}':`, error);
  }
}

/**
 * 检查DOM中是否存在指定的Web Component标签
 * @param {Object} componentConfig 组件配置
 * @returns {Boolean} 是否找到组件
 */
function checkComponentExists(componentConfig) {
  const { tag } = componentConfig;

  // 检查文档中是否存在该组件标签
  if (document.querySelector(tag)) {
    loadComponent(componentConfig);
    return true;
  }
  return false;
}

/**
 * 为特定节点检查是否包含组件
 * @param {Node} node DOM节点
 * @param {Object} componentConfig 组件配置
 * @returns {Boolean} 是否找到组件
 */
function checkNodeForComponent(node, componentConfig) {
  const { tag, observeChildren } = componentConfig;

  // 检查节点本身
  if (node.nodeType === Node.ELEMENT_NODE) {
    // 检查标签名
    if (node.tagName.toLowerCase() === tag) {
      loadComponent(componentConfig);
      return true;
    }

    // 检查子元素（如果配置允许）
    if (observeChildren && node.querySelector(tag)) {
      loadComponent(componentConfig);
      return true;
    }
  }
  return false;
}

/**
 * 初始化组件加载检测
 */
function initComponentLoader() {
  const componentsConfig = getComponentsConfig();

  // 检测函数：检查组件是否存在并加载
  const checkComponents = () => {
    // 检查是否有组件未被找到
    let allComponentsFound = true;

    for (const config of componentsConfig) {
      // 跳过已加载的组件
      if (loadedComponents.has(config.tag)) continue;

      if (!checkComponentExists(config)) {
        allComponentsFound = false;
      }
    }

    return allComponentsFound;
  };

  // 立即检查一次当前DOM
  if (checkComponents()) {
    // 如果所有组件都已找到，不需要继续观察
    return;
  }

  // 设置MutationObserver来监听DOM变化
  const observer = new MutationObserver((mutations) => {
    let shouldContinueObserving = false;

    for (const config of componentsConfig) {
      // 跳过已加载的组件
      if (loadedComponents.has(config.tag)) continue;

      // 标记需要继续观察
      shouldContinueObserving = true;

      // 检查DOM变化
      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          // 检查新增的节点
          for (const node of mutation.addedNodes) {
            if (checkNodeForComponent(node, config)) {
              // 找到组件，停止检查当前配置
              break;
            }
          }
        }
      }
    }

    // 如果所有组件都已加载，停止观察
    if (!shouldContinueObserving) {
      observer.disconnect();
    }
  });

  // 开始观察document.body的子元素变化
  observer.observe(document.body || document.documentElement, {
    childList: true,
    subtree: true,
  });

  // 页面加载完成后再检查一次
  window.addEventListener("DOMContentLoaded", () => {
    if (checkComponents()) {
      observer.disconnect();
    }
  });

  // 30秒后如果仍有未加载的组件，停止观察（防止长时间不必要的观察）
  setTimeout(() => {
    observer.disconnect();
  }, 30000);
}

// 启动加载器
initComponentLoader();

// 暴露手动加载方法，用于特殊场景下的显式加载
export function loadComponentByTag(tagName) {
  const componentsConfig = getComponentsConfig();
  const config = componentsConfig.find((c) => c.tag === tagName);

  if (config) {
    return loadComponent(config);
  } else {
    console.warn(`Component '${tagName}' not found in configuration`);
    return Promise.reject(new Error(`Component '${tagName}' not found`));
  }
}

// 导出添加组件配置的方法
export { addComponentConfig };
